import React, { useState, useMemo } from "react";
import { Table, Input, Button } from "reactstrap";

function TableView({ rows, width, height, pageSize = 5 }) {
  try {
    const tableRows = Array.isArray(rows) ? rows : [];

    if (!tableRows.length) {
      return <div className="text-center text-gray-500">No data is available.</div>;
    }

    const columns = Object.keys(tableRows[0]);

    const [filters, setFilters] = useState({}); // 👈 only per-column filters
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const [currentPage, setCurrentPage] = useState(1);

    // Format numbers
    const formatValue = (value) => {
      if (typeof value === "number") {
        return value.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      }
      return value;
    };

    // Only column-specific filters
    const filteredRows = useMemo(() => {
      return tableRows.filter((row) => {
        return Object.entries(filters).every(([col, value]) => {
          if (!value) return true;
          return String(row[col]).toLowerCase().includes(value.toLowerCase());
        });
      });
    }, [filters, tableRows]);

    // Sorting
    const sortedRows = useMemo(() => {
      if (!sortConfig.key) return filteredRows;
      return [...filteredRows].sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }, [filteredRows, sortConfig]);

    // Pagination
    const totalPages = Math.ceil(sortedRows.length / pageSize);
    const paginatedRows = sortedRows.slice(
      (currentPage - 1) * pageSize,
      currentPage * pageSize
    );

    const handleSort = (col) => {
      setSortConfig((prev) => {
        if (prev.key === col && prev.direction === "asc") {
          return { key: col, direction: "desc" };
        }
        return { key: col, direction: "asc" };
      });
    };

    return (
      <div className="max-w-6xl mx-auto">
        {/* Table */}
        <Table className="table-bordered align-middle table-nowrap mb-0">
          <thead className="table-light">
            <tr>
              {columns.map((key) => (
                <th
                  
                >
                  <div className="d-flex flex-column">
                    {/* Column Label */}
                    <div className="d-flex align-items-center text-capitalize"
                       key={key}                      
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSort(key)}
                    >
                      {key.replace(/_/g, " ")}
                      {sortConfig.key === key &&
                        (sortConfig.direction === "asc" ? (
                          <i className="ri-arrow-drop-down-line ms-1"></i>
                        ) : (
                          <i className="ri-arrow-drop-up-line ms-1"></i>
                        ))}
                    </div>

                    {/* Filter Input */}
                    <Input
                      type="text"
                      placeholder="Filter..."
                      value={filters[key] || ""}
                      onChange={(e) => {
                        setFilters({ ...filters, [key]: e.target.value });
                        setCurrentPage(1);
                      }}
                      bsSize="sm"
                      className="mt-1"
                    />
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {paginatedRows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {columns.map((key) => (
                  <td key={key}>{formatValue(row[key])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>

        {/* Pagination */}
        <div className="d-flex justify-content-between align-items-center mt-3">
          <span>
            Page {currentPage} of {totalPages}
          </span>
          <div>
            <Button
              color="secondary"
              size="sm"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Prev
            </Button>{" "}
            <Button
              color="secondary"
              size="sm"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    );
  } catch (error) {
    console.error("Error rendering TableView:", error);
    return <div style={{ color: "red" }}>Something went wrong.</div>;
  }
}

export default TableView;
